import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * Modal d'envoi de notifications aux participants d'une prestation
 *
 * Props:
 * - isOpen: boolean - État d'ouverture de la modale
 * - onClose: function - Callback de fermeture
 * - prestation: object - Données de la prestation
 * - participants: array - Liste des participants
 * - teamLeader: object - Chef d'équipe (optionnel)
 * - token: string - Token JWT
 */
const NotificationModal = ({
  isOpen,
  onClose,
  prestation,
  participants = [],
  teamLeader,
  token
}) => {
  const { showSuccess, showError } = useNotifications();

  // Onglet actif: 'send' ou 'history'
  const [activeTab, setActiveTab] = useState('send');

  // États pour l'envoi
  const [notificationType, setNotificationType] = useState('update');
  const [recipientMode, setRecipientMode] = useState('all');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [includeLeader, setIncludeLeader] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  // États pour l'historique
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Charger l'historique à l'ouverture
  useEffect(() => {
    if (isOpen && prestation?.id) {
      loadHistory();
    }
  }, [isOpen, prestation?.id]);

  // Réinitialiser à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setActiveTab('send');
      setNotificationType('update');
      setRecipientMode('all');
      setSelectedRecipients([]);
      setIncludeLeader(false);
      setCustomMessage('');
    }
  }, [isOpen]);

  const loadHistory = async () => {
    if (!prestation?.id) return;

    setLoadingHistory(true);
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/api/prestations/${prestation.id}/notifications`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setHistory(data.notifications || []);
      }
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  if (!isOpen) return null;

  const handleRecipientToggle = (userId) => {
    setSelectedRecipients(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRecipients.length === participants.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(participants.map(p => p.id || p.user_id));
    }
  };

  const handleSend = async () => {
    // Validation
    if (recipientMode === 'selection' && selectedRecipients.length === 0) {
      showError('Veuillez sélectionner au moins un destinataire');
      return;
    }

    setSending(true);

    try {
      const body = {
        notification_type: notificationType,
        recipients: recipientMode === 'all' ? 'all' :
                   recipientMode === 'leader' ? 'leader' :
                   selectedRecipients,
        include_leader: includeLeader && recipientMode !== 'leader',
        custom_message: customMessage.trim() || null
      };

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/api/prestations/${prestation.id}/notify`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        }
      );

      const data = await response.json();

      if (data.success) {
        showSuccess(data.message);
        // Recharger l'historique après envoi
        await loadHistory();
        // Basculer vers l'onglet historique pour montrer l'envoi
        setActiveTab('history');
        // Réinitialiser le formulaire
        setNotificationType('update');
        setRecipientMode('all');
        setSelectedRecipients([]);
        setIncludeLeader(false);
        setCustomMessage('');
      } else {
        showError(data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setSending(false);
    }
  };

  // Formater la date pour l'historique
  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Labels des types de notification
  const typeLabels = {
    'update': 'Modification',
    'new_assignment': 'Nouvelle affectation',
    'reminder': 'Rappel',
    'custom': 'Message personnalisé'
  };

  // Labels des statuts
  const statusLabels = {
    'sent': { label: 'Envoyé', color: '#28a745' },
    'failed': { label: 'Échec', color: '#dc3545' },
    'partial_failure': { label: 'Partiel', color: '#ffc107' }
  };

  const notificationTypes = [
    { value: 'update', label: 'Modification', description: 'Informer d\'un changement (horaire, lieu...)' },
    { value: 'new_assignment', label: 'Nouvelle affectation', description: 'Informer d\'une nouvelle affectation' },
    { value: 'reminder', label: 'Rappel', description: 'Rappeler les informations de la prestation' },
    { value: 'custom', label: 'Message personnalisé', description: 'Envoyer un message libre' }
  ];

  const activeParticipants = participants.filter(p => p.receive_notifications !== false);
  const inactiveCount = participants.length - activeParticipants.length;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Notifications</h3>
          <button onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>

        {/* Onglets */}
        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'send' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('send')}
          >
            Envoyer
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'history' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('history')}
          >
            Historique {history.length > 0 && `(${history.length})`}
          </button>
        </div>

        <div style={styles.content}>
          {/* Info prestation */}
          <div style={styles.prestationInfo}>
            <strong>{prestation.prestation_number}</strong>
            {prestation.client_name && ` - ${prestation.client_name}`}
          </div>

          {/* Onglet Envoi */}
          {activeTab === 'send' && (
            <>
          {/* Type de notification */}
          <div style={styles.section}>
            <label style={styles.label}>Type de notification</label>
            <div style={styles.radioGroup}>
              {notificationTypes.map(type => (
                <label key={type.value} style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="notificationType"
                    value={type.value}
                    checked={notificationType === type.value}
                    onChange={e => setNotificationType(e.target.value)}
                  />
                  <span style={styles.radioText}>
                    <strong>{type.label}</strong>
                    <small style={styles.radioDesc}>{type.description}</small>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Destinataires */}
          <div style={styles.section}>
            <label style={styles.label}>Destinataires</label>
            <div style={styles.recipientModes}>
              <button
                style={{
                  ...styles.modeBtn,
                  ...(recipientMode === 'all' ? styles.modeBtnActive : {})
                }}
                onClick={() => setRecipientMode('all')}
              >
                Tous ({activeParticipants.length})
              </button>
              {teamLeader && (
                <button
                  style={{
                    ...styles.modeBtn,
                    ...(recipientMode === 'leader' ? styles.modeBtnActive : {})
                  }}
                  onClick={() => setRecipientMode('leader')}
                >
                  Chef d'équipe seul
                </button>
              )}
              <button
                style={{
                  ...styles.modeBtn,
                  ...(recipientMode === 'selection' ? styles.modeBtnActive : {})
                }}
                onClick={() => setRecipientMode('selection')}
              >
                Sélection manuelle
              </button>
            </div>

            {/* Liste des participants (mode sélection) */}
            {recipientMode === 'selection' && (
              <div style={styles.participantList}>
                <div style={styles.selectAllRow}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedRecipients.length === participants.length}
                      onChange={handleSelectAll}
                    />
                    <span>Tout sélectionner</span>
                  </label>
                </div>
                {participants.map(p => {
                  const userId = p.id || p.user_id;
                  const isInactive = p.receive_notifications === false;
                  return (
                    <label
                      key={userId}
                      style={{
                        ...styles.checkboxLabel,
                        ...(isInactive ? styles.inactiveParticipant : {})
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRecipients.includes(userId)}
                        onChange={() => handleRecipientToggle(userId)}
                        disabled={isInactive}
                      />
                      <span>
                        {p.first_name} {p.last_name}
                        {isInactive && <small style={styles.inactiveTag}> (notifications désactivées)</small>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Option inclure chef d'équipe */}
            {recipientMode !== 'leader' && teamLeader && (
              <label style={{ ...styles.checkboxLabel, marginTop: '10px' }}>
                <input
                  type="checkbox"
                  checked={includeLeader}
                  onChange={e => setIncludeLeader(e.target.checked)}
                />
                <span>Inclure le chef d'équipe ({teamLeader.first_name} {teamLeader.last_name})</span>
              </label>
            )}

            {inactiveCount > 0 && recipientMode === 'all' && (
              <div style={styles.warning}>
                {inactiveCount} participant(s) ne recevront pas la notification (notifications désactivées)
              </div>
            )}
          </div>

          {/* Message personnalisé */}
          <div style={styles.section}>
            <label style={styles.label}>
              Message personnalisé
              <small style={styles.optional}> (optionnel)</small>
            </label>
            <textarea
              value={customMessage}
              onChange={e => setCustomMessage(e.target.value)}
              placeholder="Ajouter un message qui sera inclus dans l'email..."
              style={styles.textarea}
              rows={3}
            />
          </div>
            </>
          )}

          {/* Onglet Historique */}
          {activeTab === 'history' && (
            <div style={styles.historyContainer}>
              {loadingHistory ? (
                <div style={styles.loading}>Chargement...</div>
              ) : history.length === 0 ? (
                <div style={styles.emptyHistory}>
                  Aucune notification envoyée pour cette prestation.
                </div>
              ) : (
                <div style={styles.historyList}>
                  {history.map((item, index) => {
                    const status = statusLabels[item.status] || { label: item.status, color: '#6c757d' };
                    return (
                      <div key={item.id || index} style={styles.historyItem}>
                        <div style={styles.historyHeader}>
                          <span style={styles.historyType}>
                            {typeLabels[item.notification_type] || item.notification_type}
                          </span>
                          <span style={{
                            ...styles.historyStatus,
                            backgroundColor: status.color
                          }}>
                            {status.label}
                          </span>
                        </div>
                        <div style={styles.historyMeta}>
                          <span>{formatDateTime(item.sent_at)}</span>
                          <span style={styles.separator}>•</span>
                          <span>{item.recipient_count} destinataire(s)</span>
                          <span style={styles.separator}>•</span>
                          <span>par {item.sender}</span>
                        </div>
                        {item.custom_message && (
                          <div style={styles.historyMessage}>
                            "{item.custom_message}"
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelBtn} disabled={sending}>
            Fermer
          </button>
          {activeTab === 'send' && (
            <button
              onClick={handleSend}
              style={styles.sendBtn}
              disabled={sending || (recipientMode === 'selection' && selectedRecipients.length === 0)}
            >
              {sending ? 'Envoi en cours...' : 'Envoyer'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '500px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    borderBottom: '1px solid #eee'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '0 5px'
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #eee',
    padding: '0 20px'
  },
  tab: {
    padding: '12px 20px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    transition: 'all 0.2s'
  },
  tabActive: {
    color: '#007bff',
    borderBottomColor: '#007bff'
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  prestationInfo: {
    backgroundColor: '#f8f9fa',
    padding: '10px 15px',
    borderRadius: '5px',
    marginBottom: '20px',
    fontSize: '14px'
  },
  section: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontWeight: '600',
    marginBottom: '8px',
    fontSize: '14px'
  },
  optional: {
    fontWeight: 'normal',
    color: '#666'
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    backgroundColor: '#fafafa'
  },
  radioText: {
    display: 'flex',
    flexDirection: 'column'
  },
  radioDesc: {
    color: '#666',
    fontSize: '12px',
    marginTop: '2px'
  },
  recipientModes: {
    display: 'flex',
    gap: '8px',
    marginBottom: '10px'
  },
  modeBtn: {
    padding: '8px 16px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s'
  },
  modeBtnActive: {
    backgroundColor: '#007bff',
    color: 'white',
    borderColor: '#007bff'
  },
  participantList: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #ddd',
    borderRadius: '5px',
    padding: '10px'
  },
  selectAllRow: {
    borderBottom: '1px solid #eee',
    paddingBottom: '8px',
    marginBottom: '8px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '5px 0',
    cursor: 'pointer',
    fontSize: '14px'
  },
  inactiveParticipant: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  inactiveTag: {
    color: '#999',
    fontStyle: 'italic'
  },
  warning: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '8px 12px',
    borderRadius: '5px',
    fontSize: '13px',
    marginTop: '10px'
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '15px 20px',
    borderTop: '1px solid #eee'
  },
  cancelBtn: {
    padding: '10px 20px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '14px'
  },
  sendBtn: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '5px',
    backgroundColor: '#28a745',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  // Styles pour l'historique
  historyContainer: {
    minHeight: '200px'
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    padding: '40px 0'
  },
  emptyHistory: {
    textAlign: 'center',
    color: '#999',
    padding: '40px 0',
    fontStyle: 'italic'
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  historyItem: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '12px 15px',
    border: '1px solid #e9ecef'
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  historyType: {
    fontWeight: '600',
    fontSize: '14px'
  },
  historyStatus: {
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '12px',
    color: 'white',
    fontWeight: '500'
  },
  historyMeta: {
    fontSize: '12px',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px'
  },
  separator: {
    margin: '0 2px'
  },
  historyMessage: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#495057',
    fontStyle: 'italic',
    backgroundColor: 'white',
    padding: '8px 10px',
    borderRadius: '4px',
    borderLeft: '3px solid #17a2b8'
  }
};

export default NotificationModal;
